import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { type MyDefect, myDefectsApi } from './runs-api.js';

// #192: Die Worklist des Testers zeigt ALLE seine Defects — die aus eigenen Läufen (`my-defects`)
// und die, die er selbst angelegt hat (`my-reported`, auch ohne Lauf). Ein Defect aus dem eigenen
// Lauf, den der Tester auch selbst angelegt hat, steht in beiden Listen und darf nur einmal
// erscheinen. Neueste zuerst (Anlagezeitpunkt des Defects, nicht des Laufs — manuelle Defects haben
// keinen Lauf); bei gleichem Zeitstempel entscheidet die Nummer absteigend, damit die Reihenfolge
// stabil bleibt.
export function mergeMyDefects(fromRuns: MyDefect[], reported: MyDefect[]): MyDefect[] {
  const byId = new Map<string, MyDefect>();
  for (const d of [...fromRuns, ...reported]) {
    // Die erste Fundstelle gewinnt: beide Listen liefern dieselben Spalten aus demselben Defect.
    if (!byId.has(d.defect_id)) byId.set(d.defect_id, d);
  }
  return [...byId.values()].sort((a, b) => {
    const byTime = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    if (byTime !== 0) return byTime;
    return b.human_id.localeCompare(a.human_id, undefined, { numeric: true });
  });
}

// Query-Keys bewusst unverändert (`['my-defects', p]`, `['my-reported', p]`): execution-panel.tsx
// invalidiert den ersten nach dem Melden, das Dashboard liest den zweiten.
export function useMyDefects(projectId: string): {
  data: MyDefect[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const enabled = Boolean(projectId);
  const [fromRuns, reported] = useQueries({
    queries: [
      {
        queryKey: ['my-defects', projectId],
        queryFn: () => myDefectsApi.list(projectId),
        enabled,
      },
      {
        queryKey: ['my-reported', projectId],
        queryFn: () => myDefectsApi.myReported(projectId),
        enabled,
      },
    ],
  });
  // Memoisiert: ohne useMemo wäre `data` bei jedem Render ein neues Array und würde Effekte und
  // abgeleitete Memos der Aufrufer jedes Mal auslösen.
  const fromRunsData = fromRuns.data;
  const reportedData = reported.data;
  const data = useMemo(
    () => mergeMyDefects(fromRunsData ?? [], reportedData ?? []),
    [fromRunsData, reportedData],
  );
  return {
    data,
    isLoading: fromRuns.isLoading || reported.isLoading,
    isError: fromRuns.isError || reported.isError,
    refetch: () => {
      void fromRuns.refetch();
      void reported.refetch();
    },
  };
}
